import { useState, useEffect } from 'react'
import { X, Download } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

export function PWAInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null)
  const [showPrompt, setShowPrompt] = useState(false)
  const [isInstalled, setIsInstalled] = useState(false)

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true)
      return
    }

    // Check if user has dismissed the prompt before
    const dismissed = localStorage.getItem('pwa-install-dismissed')
    if (dismissed === 'true') {
      return
    }

    const handleBeforeInstallPrompt = (e: Event) => {
      // Prevent the default browser prompt
      e.preventDefault()
      // Store the event for later use
      setDeferredPrompt(e as BeforeInstallPromptEvent)
      // Show our custom prompt after a longer delay (10 seconds)
      setTimeout(() => setShowPrompt(true), 10000)
    }

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)

    // Check for iOS
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream
    if (isIOS && !isInstalled) {
      // Show iOS-specific instructions after a longer delay
      setTimeout(() => setShowPrompt(true), 10000)
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
    }
  }, [isInstalled])

  const handleInstall = async () => {
    if (!deferredPrompt) {
      // iOS or unsupported browser
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream
      if (isIOS) {
        alert('To install: Tap the Share button below and select "Add to Home Screen"')
      }
      return
    }

    // Show the browser install prompt
    deferredPrompt.prompt()
    
    // Wait for the user's response
    const { outcome } = await deferredPrompt.userChoice
    
    if (outcome === 'accepted') {
      console.log('User accepted the install prompt')
      setShowPrompt(false)
    }
    
    // Clear the deferred prompt
    setDeferredPrompt(null)
  }

  const handleDismiss = () => {
    setShowPrompt(false)
    localStorage.setItem('pwa-install-dismissed', 'true')
  }

  if (!showPrompt || isInstalled) {
    return null
  }

  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream

  return (
    <div className={cn(
      "fixed bottom-0 left-0 right-0 z-[100]",
      "flex justify-center p-4"
    )}>
      <div className={cn(
        "bg-white/80 backdrop-blur-md border border-gray-100/50",
        "text-gray-900 rounded-full px-4 py-2",
        "flex items-center gap-3 shadow-lg",
        "animate-in slide-in-from-bottom-5 duration-300",
        "text-sm max-w-fit"
      )}>
      <Download className="h-4 w-4 text-gray-600" />
      <span className="text-xs font-medium text-gray-700">
        {isIOS ? 'Add to Home Screen' : 'Install App'}
      </span>
      <Button
        size="sm"
        variant="ghost"
        onClick={handleInstall}
        className="h-auto py-0 px-2 text-xs text-gray-900 hover:text-gray-900 hover:bg-gray-100/50"
      >
        {isIOS ? 'How?' : 'Install'}
      </Button>
      <button
        onClick={handleDismiss}
        className="text-gray-400 hover:text-gray-600 transition-colors"
        aria-label="Dismiss"
      >
        <X className="h-3 w-3" />
      </button>
      </div>
    </div>
  )
}